// src/components/switchSuppliers/SwitchSupplierList.jsx

import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
  Avatar,
  Link,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Business,
  Language,
  OpenInNew,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import ConfirmDialog from "../ui/ConfirmDialog";

const SwitchSupplierList = ({ onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: suppliers, loading } = useCollection("switchSuppliers");
  const [localSuppliers, setLocalSuppliers] = useState(null);
  const { remove } = useAuditFirestore("switchSuppliers");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  React.useEffect(() => {
    if (suppliers) {
      setLocalSuppliers(suppliers);
    }
  }, [suppliers]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = localSuppliers?.filter(
    (s) =>
      s.isActive !== false &&
      (search === "" ||
        s.name?.toLowerCase().includes(search.toLowerCase()) ||
        s.web?.toLowerCase().includes(search.toLowerCase()))
  );

  const paginatedSuppliers = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.split(" ");
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.error.main,
    ];
    
    if (!name) return colors[0];
    
    const sum = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const formatUrl = (url) => {
    if (!url) return "";
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return `https://${url}`;
    }
    return url;
  };

  if (loading || !localSuppliers) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
        >
          {/* SEARCH */}
          <TextField
            placeholder="Buscar por nombre o web..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 320, md: 400 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
                "& fieldset": {
                  borderColor: theme.palette.divider,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<Business />}
              label={`${filtered?.length || 0} comercializadoras`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 600,
                background: `${theme.palette.primary.main}15`,
                color: theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}40`,
                "& .MuiChip-icon": {
                  color: theme.palette.primary.main,
                },
              }}
            />

            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (suppliers) setLocalSuppliers(suppliers);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.main}10`,
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabla */}
      <Card
        elevation={2}
        sx={{
          borderRadius: { xs: 1, sm: 2 },
          overflow: "hidden",
          border: "1px solid",
          borderColor: theme.palette.divider,
          background: theme.palette.background.paper,
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow
                sx={{
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(0, 175, 163, 0.1)"
      : "rgba(0, 95, 101, 0.05)",
}}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: { xs: 50, sm: 60 },
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  {isMobile ? "" : "Logo"}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Nombre
                </TableCell>
                {!isMobile && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: "0.85rem",
                    }}
                  >
                    Web
                  </TableCell>
                )}
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    width: { xs: 80, sm: 100 },
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} align="center" sx={{ py: 8 }}>
                    <Business 
                      sx={{ 
                        fontSize: 64, 
                        color: theme.palette.primary.main,
                        opacity: 0.3,
                        mb: 2
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.text.secondary,
                        mb: 1
                      }}
                    >
                      {search ? "Sin resultados" : "Sin comercializadoras"}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {search 
                        ? "Prueba con otros términos de búsqueda" 
                        : "Añade tu primera comercializadora"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSuppliers?.map((supplier) => (
                  <TableRow
                    key={supplier.id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(255, 152, 0, 0.02)",
                      },
                    }}
                  >
                    <TableCell>
                      <Avatar
                        sx={{
                          width: { xs: 36, sm: 44 },
                          height: { xs: 36, sm: 44 },
                          background: `linear-gradient(135deg, ${getAvatarColor(supplier.name)} 0%, ${getAvatarColor(supplier.name)}dd 100%)`,
                          fontSize: { xs: "0.9rem", sm: "1.1rem" },
                          fontWeight: 600,
                          boxShadow: `0 2px 8px ${getAvatarColor(supplier.name)}40`,
                        }}
                      >
                        {getInitials(supplier.name)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                      >
                        {supplier.name || "Sin nombre"}
                      </Typography>
                      {isMobile && supplier.web && (
                        <Link
                          href={formatUrl(supplier.web)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            fontSize: "0.7rem",
                            color: theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <Language sx={{ fontSize: 12 }} />
                          {supplier.web}
                        </Link>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        {supplier.web ? (
                          <Link
                            href={formatUrl(supplier.web)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.primary.main,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontWeight: 500,
                              "&:hover": {
                                color: theme.palette.primary.dark,
                              },
                            }}
                          >
                            <Language sx={{ fontSize: 16 }} />
                            {supplier.web}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(supplier);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(supplier);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {filtered?.length > 0 && (
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
            }}
          />
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalSuppliers((prev) =>
            prev.filter((s) => s.id !== deleteTarget.id)
          );
          const deletedSupplier = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedSupplier.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalSuppliers((prev) => [...prev, deletedSupplier]);
          }
        }}
        title="Eliminar Comercializadora"
        message={`¿Eliminar a ${deleteTarget?.name || "esta comercializadora"}?`}
        type="delete"
      />
    </Box>
  );
};

export default SwitchSupplierList;